import { useMemo, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  PageHeader,
} from '@/components/ui'
import {
  CustomerMasterDataSync,
  PORTAL_LABEL,
  countPushableDiffs,
  useMasterDataSyncOverview,
  type Portal,
  type PortalComparison,
} from './MasterDataSyncPanel.tsx'

const PORTAL_DOT: Record<Portal, string> = {
  eset: 'bg-green-500',
  also: 'bg-blue-500',
}

export function MasterDataSyncPage() {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [showOnlyDiffs, setShowOnlyDiffs] = useState(true)

  const query = useMasterDataSyncOverview()

  const filteredCustomers = useMemo(() => {
    if (!query.data) return []
    const term = search.trim().toLowerCase()
    return query.data.customers.filter((c) => {
      if (term && !c.customerName.toLowerCase().includes(term)) return false
      if (showOnlyDiffs && countPushableDiffs(c) === 0) return false
      return true
    })
  }, [query.data, search, showOnlyDiffs])

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const totalDiffs = useMemo(() => {
    if (!query.data) return 0
    return query.data.customers.reduce((s, c) => s + countPushableDiffs(c), 0)
  }, [query.data])

  return (
    <div className="space-y-4">
      <PageHeader
        title="Stammdaten-Abgleich"
        description="Vergleicht die Kundenstammdaten zwischen SAP und den MSP-Portalen. Bei Abweichungen können einzelne Felder oder ganze Datensätze von SAP in das jeweilige Portal übertragen werden."
        breadcrumbs={[
          { label: 'Beschaffung' },
          { label: 'Lieferanten / MSP', to: '/einkauf/lieferanten' },
          { label: 'Stammdaten-Abgleich' },
        ]}
        actions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${query.isFetching ? 'animate-spin' : ''}`} />
            Neu laden
          </Button>
        }
      />

      {query.data ? (
        <Card>
          <Card.Body>
            <div className="grid gap-3 sm:grid-cols-3">
              <SystemStatusTile label="SAP" status={query.data.sap} />
              {query.data.portals.map((p) => (
                <SystemStatusTile
                  key={p.id}
                  label={PORTAL_LABEL[p.id]}
                  status={p}
                  dotClass={PORTAL_DOT[p.id]}
                />
              ))}
            </div>
          </Card.Body>
        </Card>
      ) : null}

      <Card>
        <Card.Body className="flex flex-wrap items-center gap-3">
          <Input
            placeholder="Kunde suchen..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
          />
          <label className="flex items-center gap-2 text-small text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyDiffs}
              onChange={(e) => setShowOnlyDiffs(e.target.checked)}
              className="h-4 w-4"
            />
            Nur Kunden mit Abweichungen
          </label>
          <div className="ml-auto text-small text-gray-600">
            {totalDiffs > 0 ? (
              <span className="flex items-center gap-1">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                {totalDiffs} pushbare Abweichung{totalDiffs === 1 ? '' : 'en'}
              </span>
            ) : query.data ? (
              <span className="flex items-center gap-1">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Alles synchron
              </span>
            ) : null}
          </div>
        </Card.Body>
      </Card>

      {query.isLoading ? (
        <LoadingState label="Stammdaten werden verglichen..." />
      ) : query.isError ? (
        <ErrorState
          title="Vergleich fehlgeschlagen"
          description={
            query.error instanceof Error ? query.error.message : 'Unbekannter Fehler'
          }
          onRetry={() => query.refetch()}
        />
      ) : filteredCustomers.length === 0 ? (
        <EmptyState
          title={
            showOnlyDiffs ? 'Keine Abweichungen' : 'Keine verlinkten Kunden gefunden'
          }
          description={
            showOnlyDiffs
              ? 'Alle Kunden mit SAP-Verknüpfung und Portal-Anbindung sind synchron.'
              : 'Es gibt keine Kunden, die sowohl in SAP als auch in einem MSP-Portal verknüpft sind.'
          }
        />
      ) : (
        <Card>
          <ul className="divide-y divide-gray-200">
            {filteredCustomers.map((c) => {
              const isOpen = expanded.has(c.customerId)
              const totalCustomerDiffs = countPushableDiffs(c)
              return (
                <li key={c.customerId}>
                  <button
                    type="button"
                    onClick={() => toggleExpanded(c.customerId)}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 text-gray-500 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-body text-gray-900 truncate">
                        {c.customerName}
                      </div>
                      <div className="text-caption text-gray-500">
                        SAP-CardCode: {c.sapCardCode ?? '—'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {c.portals.map((p) => (
                        <PortalBadge key={p.portal} portal={p} />
                      ))}
                      {totalCustomerDiffs > 0 ? (
                        <Badge variant="warning" size="sm">
                          {totalCustomerDiffs} Diff
                        </Badge>
                      ) : (
                        <Badge variant="success" size="sm">
                          synchron
                        </Badge>
                      )}
                    </div>
                  </button>
                  {isOpen ? (
                    <div className="px-4 pb-4 bg-gray-50">
                      <CustomerMasterDataSync comparison={c} />
                    </div>
                  ) : null}
                </li>
              )
            })}
          </ul>
        </Card>
      )}
    </div>
  )
}

// ─── Subkomponenten ─────────────────────────────────────────────────────────

interface SystemStatus {
  configured: boolean
  available: boolean
  error?: string
}

function SystemStatusTile({
  label,
  status,
  dotClass,
}: {
  label: string
  status: SystemStatus
  dotClass?: string
}) {
  const ok = status.configured && status.available && !status.error
  return (
    <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-white p-3">
      <span
        className={`mt-1 h-2 w-2 rounded-full shrink-0 ${dotClass ?? 'bg-slate-700'}`}
      />
      <div className="flex-1 min-w-0">
        <div className="text-small font-medium text-gray-900">{label}</div>
        <div className="flex items-center gap-1 text-caption">
          {ok ? (
            <>
              <CheckCircle2 className="h-3 w-3 text-green-500" />
              <span className="text-gray-600">Verbunden</span>
            </>
          ) : status.configured ? (
            <>
              <XCircle className="h-3 w-3 text-red-500" />
              <span className="text-red-600 truncate" title={status.error ?? ''}>
                {status.error ?? 'Nicht verfügbar'}
              </span>
            </>
          ) : (
            <>
              <AlertTriangle className="h-3 w-3 text-gray-400" />
              <span className="text-gray-500">Nicht konfiguriert</span>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function PortalBadge({ portal }: { portal: PortalComparison }) {
  if (!portal.configured) {
    return (
      <Badge variant="neutral" size="sm">
        {PORTAL_LABEL[portal.portal]} aus
      </Badge>
    )
  }
  if (portal.error || !portal.available) {
    return (
      <Badge variant="danger" size="sm">
        {PORTAL_LABEL[portal.portal]}: Fehler
      </Badge>
    )
  }
  if (portal.pushableDiffCount > 0) {
    return (
      <Badge variant="warning" size="sm">
        {PORTAL_LABEL[portal.portal]}: {portal.pushableDiffCount}
      </Badge>
    )
  }
  return (
    <Badge variant="success" size="sm">
      {PORTAL_LABEL[portal.portal]} ok
    </Badge>
  )
}
